import type { MDExportPlugin } from '../types';

/**
 * Registry for managing plugins
 */
export class PluginRegistry {
  private plugins: Map<string, MDExportPlugin> = new Map();

  /**
   * Register a plugin
   */
  register(plugin: MDExportPlugin): void {
    if (this.plugins.has(plugin.name)) {
      throw new Error(`Plugin ${plugin.name} is already registered`);
    }

    // Validate plugin
    this.validatePlugin(plugin);

    this.plugins.set(plugin.name, plugin);
  }

  /**
   * Unregister a plugin
   */
  unregister(name: string): void {
    this.plugins.delete(name);
  }

  /**
   * Get a plugin by name
   */
  get(name: string): MDExportPlugin | undefined {
    return this.plugins.get(name);
  }

  /**
   * Get all registered plugins
   */
  getPlugins(): MDExportPlugin[] {
    return Array.from(this.plugins.values());
  }

  /**
   * Check if a plugin is registered
   */
  has(name: string): boolean {
    return this.plugins.has(name);
  }

  /**
   * Get plugin names
   */
  getPluginNames(): string[] {
    return Array.from(this.plugins.keys());
  }

  /**
   * Clear all plugins
   */
  clear(): void {
    this.plugins.clear();
  }

  /**
   * Validate plugin structure
   */
  private validatePlugin(plugin: MDExportPlugin): void {
    if (!plugin.name) {
      throw new Error('Plugin must have a name');
    }

    if (!plugin.version) {
      throw new Error('Plugin must have a version');
    }

    // Validate hooks if present
    if (plugin.hooks) {
      const validHooks = [
        'beforeParse',
        'afterParse',
        'beforeRender',
        'afterRender',
        'beforePDF',
        'afterPDF',
      ];

      for (const hook of Object.keys(plugin.hooks)) {
        if (!validHooks.includes(hook)) {
          throw new Error(`Invalid hook: ${hook}`);
        }
      }
    }

    // Validate transformers if present
    if (plugin.transformers) {
      if (plugin.transformers.remark && !Array.isArray(plugin.transformers.remark)) {
        throw new Error('remark transformers must be an array');
      }

      if (plugin.transformers.rehype && !Array.isArray(plugin.transformers.rehype)) {
        throw new Error('rehype transformers must be an array');
      }
    }
  }
}
